// Contract for the PUBLIC end-user report (/r/:token) — the SMS-delivered
// personal snapshot that replaces the broken Data Studio reports.
//
// A snapshot is a small, self-contained, PRE-AGGREGATED JSON written by the
// generator (SideNerdApps scripts/reports/generate_user_report.py) to a
// token-keyed object in the config bucket:  ui/reports/{token}.json, fetched here
// at ${VITE_CONFIG_BASE_URL}/reports/{token}.json.
//
// SECURITY MODEL (v1, intentionally simple — see the report system doc):
//   - The unguessable token in the URL IS the grant ("this device's data").
//   - A snapshot contains ONLY that person's aggregates + first name — never raw
//     phone numbers, never other people, never row-level PII. So a leaked link
//     exposes one person's totals, nothing else.
//   - Links expire via an S3 lifecycle rule on the reports/ prefix.
//   - No Cognito: end users interact by SMS; the number is the identity.
// Future: signed/expiring URLs or an SMS one-time-code exchange for the token.

import { env } from "../env";
import { brand } from "../brand";

export interface ReportSummaryStat {
  label: string;
  value: number | string;
  unit?: string;
}

export interface ReportSlice {
  label: string;
  value: number;
}

export interface ReportBreakdown {
  /** e.g. "Hours by role". */
  title: string;
  unit: string; // e.g. "hrs"
  slices: ReportSlice[];
}

export interface UserReportSnapshot {
  schema: "user-report/v1";
  project: string; // project_string, e.g. "ither_ither"
  reportType?: string; // "volunteer" | "donation"
  appName: string; // "Ither"
  title: string; // "Volunteer Activity Report" / "Donor Activity Report"
  brand?: { logoUrl?: string; accent?: string; accentInk?: string; onAccent?: string };
  person?: { firstName?: string };
  generatedAt: string; // ISO timestamp
  periodLabel?: string; // "Lifetime", "Apr–Jul 2026", …
  summary: ReportSummaryStat[]; // the Lifetime Summary tiles
  breakdowns: ReportBreakdown[]; // the pie/donut charts
  footerNote?: string;
}

/** Deterministic offline sample so /r/demo renders without a backend. */
export const sampleSnapshot: UserReportSnapshot = {
  schema: "user-report/v1",
  project: "ither_ither",
  reportType: "volunteer",
  appName: "Ither",
  title: "Volunteer Activity Report",
  brand: {
    logoUrl: "https://integrationlogos.s3.us-east-1.amazonaws.com/ither128.png",
    accent: brand.products.ither.primary,
    accentInk: brand.products.ither.primaryInk,
    onAccent: brand.products.ither.onPrimary,
  },
  person: { firstName: "Dana" },
  generatedAt: new Date().toISOString(),
  periodLabel: "Lifetime",
  summary: [
    { label: "Volunteer hours", value: 128.5, unit: "hrs" },
    { label: "Organizations", value: 4 },
    { label: "Activities logged", value: 37 },
    { label: "Donations", value: 250, unit: "$" },
  ],
  breakdowns: [
    {
      title: "Hours by role",
      unit: "hrs",
      slices: [
        { label: "Mentor", value: 52 },
        { label: "Driver", value: 34 },
        { label: "Event Support", value: 26.5 },
        { label: "Kitchen", value: 16 },
      ],
    },
    {
      title: "Hours by organization",
      unit: "hrs",
      slices: [
        { label: "Food Bank", value: 61 },
        { label: "Youth Center", value: 39.5 },
        { label: "Community Garden", value: 28 },
      ],
    },
    {
      title: "Hours by cause",
      unit: "hrs",
      slices: [
        { label: "Hunger", value: 70 },
        { label: "Children & Youth", value: 33.5 },
        { label: "Community Development", value: 25 },
      ],
    },
  ],
  footerNote: "A snapshot of your activity, generated from your text-message logs.",
};

/** Donor variant sample (/r/demo-donor) — dollars-focused, matching the old PDF. */
export const sampleDonorSnapshot: UserReportSnapshot = {
  schema: "user-report/v1",
  project: "ither_ither",
  reportType: "donation",
  appName: "Ither",
  title: "Donor Activity Report",
  brand: {
    logoUrl: "https://integrationlogos.s3.us-east-1.amazonaws.com/ither128.png",
    accent: brand.products.ither.primary,
    accentInk: brand.products.ither.primaryInk,
    onAccent: brand.products.ither.onPrimary,
  },
  person: { firstName: "Dana" },
  generatedAt: new Date().toISOString(),
  periodLabel: "Lifetime",
  summary: [
    { label: "Total donated", value: 1240, unit: "$" },
    { label: "Organizations", value: 3 },
    { label: "Gifts", value: 11 },
  ],
  breakdowns: [
    {
      title: "Donations by organization",
      unit: "$",
      slices: [
        { label: "Food Bank", value: 620 },
        { label: "Youth Center", value: 380 },
        { label: "Community Garden", value: 240 },
      ],
    },
    {
      title: "Donations by cause",
      unit: "$",
      slices: [
        { label: "Hunger", value: 700 },
        { label: "Children & Youth", value: 340 },
        { label: "Community Development", value: 200 },
      ],
    },
  ],
  footerNote: "A snapshot of your giving, generated from your text-message logs.",
};

/**
 * Fetch a snapshot by token. Falls back to a sample when there's no config base
 * URL (offline dev) or a special demo token ("demo" = volunteer, "demo-donor" =
 * donor). Missing/expired links surface a friendly error (the SPA rewrite serves
 * index.html for unknown keys, so we guard against an HTML body).
 */
export async function fetchSnapshot(token: string): Promise<UserReportSnapshot> {
  const base = env.configBaseUrl;
  if (token === "demo-donor") return sampleDonorSnapshot;
  if (!base || token === "demo") return sampleSnapshot;

  let res: Response;
  try {
    res = await fetch(`${base}/reports/${encodeURIComponent(token)}.json`, {
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new Error("Couldn't reach the report service. Please try again.");
  }
  if (!res.ok) {
    throw new Error(
      res.status === 404
        ? "This report link has expired or is no longer available."
        : `Couldn't load this report (${res.status}).`,
    );
  }
  const contentType = res.headers.get("content-type") ?? "";
  const text = await res.text();
  if (contentType.includes("text/html") || text.trimStart().startsWith("<")) {
    throw new Error("This report link has expired or is no longer available.");
  }
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("This report couldn't be read.");
  }
  if (!data || (data as UserReportSnapshot).schema !== "user-report/v1") {
    throw new Error("This report is in an unsupported format.");
  }
  return data as UserReportSnapshot;
}
